import { useMemo } from 'react';
import { useAssemblySteps } from './categoryBands';
import { useHardwareCrops } from './hardwareCropsRun';

// EVERY DRAWING OF A PART IN THE STEPS, ON THE PAGE — the manual search's answers (see
// hardwareCropsRun) in the frame the pages are measured in.
//
// There used to be a second search here: Phase 3's "In the steps", one call per step asked
// about the parts the repartition gave it. It boxed the same parts on the same pages as the
// manual search in Phase 2's listing-crops row, into a store of its own. It was merged into
// that one, and what is left is the view its readers need — tool sizing (a ratio of two boxes on
// one page, against the page's own aspect) and Phase 5's step pictures (cut from the page).
//
// THE PAGE AND NOT THE BAND. The search's rectangles are 0..1 within the band it was shown —
// a full-width strip of one page, `top` to `bottom` — and these readers work on whole pages. The
// band is known from the step alone, so the move back is arithmetic and nothing is rendered.
//
// THE TIGHT BOX where there is one: the padded one is for cutting a tile, and tool sizing
// divides one box by another, where padding is not air but a thumb on the scale.
export type SchemaCrop = {
  // Which page of the manual, and where on it (0..1 from the top left).
  page: number;
  bbox: [number, number, number, number];
  // Which step drew it, by the number the manual prints.
  step: number;
  // What this drawing shows the tool working on — part indices, on a tool's crop only.
  touches?: number[];
};

// By part INDEX, like every other per-part answer in Phase 2.
export type BySchemaPart = Record<number, SchemaCrop[]>;

export function useSchemaCrops(docId: string): { byPart: BySchemaPart; busy: boolean } {
  const { byPart: found, busy } = useHardwareCrops(docId);
  const { steps } = useAssemblySteps(docId);
  const byPart = useMemo(() => {
    const byCategory = new Map(steps.map((s) => [s.category, s]));
    const out: BySchemaPart = {};
    for (const [at, crops] of Object.entries(found)) {
      out[Number(at)] = crops.flatMap((c) => {
        const step = byCategory.get(c.step);
        const page = step?.pages[c.shot];
        if (!step || page == null) return [];
        const { top, bottom } = step.bands[page] ?? { top: 0, bottom: 1 };
        const [x0, y0, x1, y1] = c.rawBbox ?? c.bbox;
        const tall = bottom - top;
        return [{
          page,
          bbox: [x0, top + y0 * tall, x1, top + y1 * tall],
          step: step.num,
          ...(c.touches ? { touches: c.touches } : {}),
        }];
      });
    }
    return out;
  }, [found, steps]);
  return { byPart, busy };
}
